import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';

const OnboardingWelcome = () => {
  const navigate = useNavigate();
  const { showWelcome, currentScenario, config, markAsSeen } = useOnboarding();

  if (!showWelcome || !currentScenario) {
    return null;
  }

  const welcomeScreen = config.welcomeScreen;
  const steps = currentScenario.steps;
  const primaryStep = steps[0]; // Primeiro step é sempre o próximo a fazer

  const handleAction = () => {
    if (primaryStep.action?.route) {
      navigate(primaryStep.action.route);
    }
    markAsSeen();
  };

  const handleClose = () => {
    markAsSeen();
  };

  return (
    <Dialog
      open={showWelcome}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {welcomeScreen.title}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {welcomeScreen.subtitle}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            color: 'text.primary',
          }}
        >
          <Typography variant="body1" paragraph>
            {welcomeScreen.description}
          </Typography>

          <Stepper orientation="vertical" activeStep={0}>
            {steps.map((step, index) => (
              <Step key={step.id} active={index === 0} completed={false}>
                <StepLabel
                  StepIconComponent={
                    index === 0 ? RadioButtonUncheckedIcon : RadioButtonUncheckedIcon
                  }
                >
                  <Typography variant="h6" fontWeight="bold">
                    {step.title}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {step.description}
                  </Typography>

                  {step.examples && step.examples.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        Exemplos:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {step.examples.map((example, idx) => (
                          <Chip
                            key={idx}
                            label={example}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} sx={{ color: 'white' }}>
          Fechar
        </Button>
        {primaryStep.action && (
          <Button
            variant="contained"
            size="large"
            onClick={handleAction}
            sx={{
              backgroundColor: 'white',
              color: 'primary.main',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            {primaryStep.action.label}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingWelcome;
